import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../api";

// What is waiting to be scored, where each paper is in the line, and whether a run is going.
//
// FETCHED ONCE AND SHARED. The Score control sits in the lesson bar and the people list sits inside
// the review console — different trees, one fact. Two components polling the same endpoint would
// ask Cloud Run twice as often for an answer neither of them owns.
//
// POLLED ONLY WHILE SOMETHING IS HAPPENING. Scoring is a Cloud Run Job with no callback, so the
// screen has no way to be told; the alternative to polling is a page that says "12 waiting" until
// somebody reloads it. It stops the moment nothing is running, which is most of the time.

export interface ScoreRunState {
  available: boolean;
  configured: boolean;
  scorable: boolean;
  states: Record<string, number>;
  waiting: number;
  /** Cloud Run says an execution is in flight. Asked, never remembered — a stale "running" is what
   *  makes somebody wait for nothing. */
  running: boolean;
  /** artifact_id -> place in line, in the order `run_scoring` will reach them (by `created_at`).
   *  Counts only the papers this teacher may see; one run drains the whole district. */
  queue: Record<string, number>;
  queue_total: number;
  requests: { request_id: string; requested_at: string | null; papers_waiting: number | null;
              execution_name: string | null }[];
}

interface Value {
  state: ScoreRunState | null;
  refresh: () => void;
}

const ScoreRunContext = createContext<Value>({ state: null, refresh: () => {} });

/** How often to ask while a run is going. Long enough not to hammer Cloud Run, short enough that a
 *  paper finishing feels like it happened rather than like a reload. */
const POLL_MS = 8000;

export function ScoreRunProvider({ activityId, children }:
    { activityId: string | null; children: React.ReactNode }) {
  const [state, setState] = useState<ScoreRunState | null>(null);
  const timer = useRef<number | null>(null);

  const read = useCallback(() => {
    if (!activityId) { setState(null); return; }
    api.get<ScoreRunState>(`/score-runs?activity_id=${encodeURIComponent(activityId)}`)
      .then(setState)
      // Not configured, not permitted, not reachable. The Score control is the only thing that
      // could act on any of them and it says so itself; the people list simply shows no queue.
      .catch(() => setState(null));
  }, [activityId]);

  useEffect(() => { setState(null); read(); }, [read]);

  useEffect(() => {
    // The condition is the whole design: a folder with a run in flight is the only state this
    // screen cannot learn about any other way.
    if (!state?.running) {
      if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
      return;
    }
    timer.current = window.setTimeout(read, POLL_MS);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [state, read]);

  return (
    <ScoreRunContext.Provider value={{ state, refresh: read }}>{children}</ScoreRunContext.Provider>
  );
}

export function useScoreRun(): Value {
  return useContext(ScoreRunContext);
}

/** What to say under a student's name while their paper is in the line.
 *
 *  WAITING IS NOT QUEUED. `bound` means the paper is ready to be scored — it does NOT mean anybody
 *  has pressed Score. Calling that "queued" would have a teacher waiting on a run that was never
 *  asked for, which is the kind of wrong that costs an afternoon rather than a click.
 */
export function queueWord(state: ScoreRunState | null, artifactId: string | null,
                          artifactState: string | null):
    { word: string; detail: string } | null {
  // THE PAPER'S OWN STATE DECIDES. The queue comes from the server and a poll is always a little
  // behind the work, so a paper can finish between two reads. Believing the queue over the state
  // would put "queued to be scored" under a paper that is already waiting for its teacher —
  // `artifact.state` is true by construction and the queue is a snapshot.
  if (artifactState !== "bound") return null;
  if (!state?.running || !artifactId) return null;
  const position = state.queue[artifactId];
  if (!position) return null;
  return {
    word: "queued to be scored",
    // The honest caveat, where somebody deciding whether to wait will see it.
    detail: `${position} of ${state.queue_total} waiting in your cohorts. `
          + `One run scores every cohort in the district, so the line may be longer than this.`,
  };
}
